// Production Summary — client-side filter combinator (Feb 2026).
//
// Extracted so the AND semantics can be exhaustively unit-tested
// without dragging in axios/React state.
//
// Contract:
//   • Time filter and Qty filter ONLY constrain non-override rows.
//     They never hide NO_SCHEDULE / COMPLETED rows — those rows have
//     no time_status to match against, but the filters aren't asking
//     them to.
//   • Override filter ONLY constrains override rows. It never hides
//     non-override rows — a user asking for "COMPLETED" is filtering
//     the override slice, not the whole table.
//   • Feb 2026 additions — Model / Brand / SKU-search are IDENTITY
//     filters. They apply to BOTH override and non-override rows
//     (a Model choice shouldn't hide a matching COMPLETED forecast).
//     Combined AND with every other dimension per prompt AC4.
//   • Empty filter string ("" / null / undefined) means "no
//     constraint" for that dimension.

/**
 * @param {Array} rows            Production Summary rows carrying
 *                                override_status / time_status / qty_status
 *                                / buyer_sku_id.
 * @param {Object} filters
 * @param {string} filters.timeFilter      "" | "OVERDUE" | "ON_TRACK"
 * @param {string} filters.qtyFilter       "" | "SHORTFALL" | "EXCESS" | "MATCHES_FORECAST"
 * @param {string} filters.overrideFilter  "" | "NO_SCHEDULE" | "COMPLETED"
 * @param {string} filters.modelFilter     "" | model_id
 * @param {string} filters.brandFilter     "" | brand_id
 * @param {string} filters.skuSearch       "" | substring (case-insensitive)
 * @param {Map}    filters.skuLookup       buyer_sku_id → {brand_id, model_id}
 * @returns {Array} filtered rows in the same order.
 */
export function filterProductionSummaryRows(rows, filters = {}) {
  if (!Array.isArray(rows)) return [];
  const {
    timeFilter = "", qtyFilter = "", overrideFilter = "",
    modelFilter = "", brandFilter = "", skuSearch = "",
    skuLookup = null,
  } = filters;
  const anyStatus = timeFilter || qtyFilter || overrideFilter;
  const anyIdentity = modelFilter || brandFilter || skuSearch;
  if (!anyStatus && !anyIdentity) return rows;
  const needle = skuSearch ? skuSearch.toLowerCase() : "";
  return rows.filter((r) => {
    if (!r) return false;
    // Identity filters — apply universally, regardless of override band.
    if (modelFilter || brandFilter) {
      const meta = skuLookup ? skuLookup.get(r.buyer_sku_id) : null;
      if (modelFilter && (!meta || meta.model_id !== modelFilter)) return false;
      if (brandFilter && (!meta || meta.brand_id !== brandFilter)) return false;
    }
    if (needle && !(r.buyer_sku_id || "").toLowerCase().includes(needle)) return false;
    // Status filters — split by override band per the original contract.
    const isOverride = !!r.override_status;
    if (isOverride) {
      if (overrideFilter && r.override_status !== overrideFilter) return false;
      return true;
    }
    if (timeFilter && r.time_status !== timeFilter) return false;
    if (qtyFilter && r.qty_status !== qtyFilter) return false;
    return true;
  });
}
